import ScrollReveal from "@/components/ScrollReveal";
import { useLang } from "@/components/LanguageContext";

// Yellow 5-star row, matching the ByNext style
function Stars() {
  return (
    <div className="flex items-center gap-0.5" aria-label="5 stars">
      {Array.from({ length: 5 }).map((_, i) => (
        <svg key={i} width="18" height="18" viewBox="0 0 20 20" className="text-[#FBBC04]" aria-hidden="true">
          <path
            fill="currentColor"
            d="M10 1.5l2.6 5.27 5.82.85-4.21 4.1.99 5.78L10 14.77 4.8 17.5l.99-5.78L1.58 7.62l5.82-.85L10 1.5z"
          />
        </svg>
      ))}
    </div>
  );
}

// Round avatar with the reviewer's first letter — ByNext style
function Avatar({ name, color }) {
  const letter = (name || "?").trim().charAt(0).toUpperCase();
  const bg = color || "#1A73E8";
  return (
    <div
      className="w-10 h-10 rounded-full grid place-items-center text-white font-medium text-[15px] select-none shrink-0"
      style={{ backgroundColor: bg }}
      aria-hidden="true"
    >
      {letter}
    </div>
  );
}

export default function GoogleReviews({ heading, reviews }) {
  const { lang } = useLang();
  if (!reviews?.length) return null;

  return (
    <section
      className="bg-white py-24 md:py-32"
      data-testid="google-reviews"
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <ScrollReveal>
          <h2
            className="text-black"
            style={{
              fontFamily:
                "'Hanken Grotesk', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
              fontWeight: 300,
              fontSize: 36,
              lineHeight: "45px",
              letterSpacing: "normal",
              whiteSpace: "pre-line",
            }}
            data-testid="google-reviews-heading"
          >
            {heading}
          </h2>
        </ScrollReveal>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {reviews.map((r, idx) => (
            <ScrollReveal key={idx} delay={idx * 50}>
              <article
                className="bg-[#F7F7F8] rounded-2xl p-7 md:p-8 h-full flex flex-col"
                data-testid={`google-review-${idx}`}
                style={{
                  fontFamily:
                    "'Hanken Grotesk', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
                }}
              >
                <div className="flex items-center gap-4 mb-5">
                  <Avatar name={r.name} color={r.color} />
                  <Stars />
                </div>
                <p
                  className="text-[15.5px] text-black leading-[1.75] flex-1"
                  style={{ fontWeight: 300 }}
                >
                  {r.text}
                </p>
                <div
                  className="mt-7 text-[16px] text-black"
                  style={{ fontWeight: 500 }}
                  data-testid={`google-review-name-${idx}`}
                >
                  {r.name}
                </div>
              </article>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}
